import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Search,
  Clock,
  Play,
  Calendar,
  ChevronRight,
  Zap,
  Filter,
  LayoutGrid,
  List,
  GraduationCap,
  Wrench,
  Droplets,
  Flame,
  Monitor,
  Tag,
  DollarSign,
  ChevronDown,
  X,
  Building2,
  Sparkles,
  Eye,
  Users,
  Share2,
  UserPlus
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ShareDialog from "../../components/shared/ShareDialog";
import BookSessionDialog from "../../components/shared/BookSessionDialog";
import AssignmentModal from "../../components/shared/AssignmentModal";

const suiteConfig = {
  "Craft Skills ILT": { icon: Wrench, color: "bg-blue-500/10 text-blue-500 border-blue-500/20", accent: "#3B82F6" },
  "Power Gen ILT": { icon: Flame, color: "bg-orange-500/10 text-orange-500 border-orange-500/20", accent: "#F97316" },
  "Craft Skills eLearning": { icon: Monitor, color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", accent: "#10B981" },
  "Power Gen eLearning": { icon: Zap, color: "bg-amber-500/10 text-amber-500 border-amber-500/20", accent: "#F59E0B" },
  "Water Treatment eLearning": { icon: Droplets, color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20", accent: "#06B6D4" },
  "Training Stations": { icon: GraduationCap, color: "bg-purple-500/10 text-purple-500 border-purple-500/20", accent: "#8B5CF6" },
};

const courseTypeIcons = {
  "scorm_1.2": Zap,
  "scorm_2004": Zap,
  ilt: Calendar,
  video: Play,
  document: BookOpen
};

const courseTypeLabels = {
  "scorm_1.2": "eLearning",
  "scorm_2004": "eLearning",
  ilt: "Instructor-Led",
  video: "Video",
  document: "Document"
};

const CatalogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSuite, setActiveSuite] = useState("all");
  const [activeSeries, setActiveSeries] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [expandedSeries, setExpandedSeries] = useState({});
  const [shareCourse, setShareCourse] = useState(null);
  const [bookCourse, setBookCourse] = useState(null);
  const [assignCourse, setAssignCourse] = useState(null);

  const isTenantAdmin = user?.role === "tenant_admin";

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [coursesRes, suitesRes] = await Promise.all([
        api.get("/courses"),
        api.get("/catalog-suites")
      ]);
      setCourses(coursesRes.data || []);
      setSuites(suitesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch catalog:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchesSearch = !searchQuery || 
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (course.description && course.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (course.code && course.code.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesSuite = activeSuite === "all" || course.catalog_suite === activeSuite;
      const matchesSeries = activeSeries === "all" || course.series === activeSeries;
      const matchesType = filterType === "all" || 
        (filterType === "elearning" && (course.course_type === "scorm_1.2" || course.course_type === "scorm_2004")) ||
        (filterType === "ilt" && course.course_type === "ilt");
      const matchesSource = filterSource === "all" ||
        (filterSource === "tts" && (!course.source || course.source === "tts")) ||
        (filterSource === "company" && course.source === "tenant") ||
        (filterSource === "premium" && (course.is_premium || course.course_type === "ilt"));
      return matchesSearch && matchesSuite && matchesSeries && matchesType && matchesSource;
    });
  }, [courses, searchQuery, activeSuite, activeSeries, filterType, filterSource]);

  const groupedBySeries = useMemo(() => {
    const grouped = {};
    filteredCourses.forEach((course) => {
      const series = course.series || "General";
      if (!grouped[series]) grouped[series] = [];
      grouped[series].push(course);
    });
    return grouped;
  }, [filteredCourses]);

  const currentSeries = useMemo(() => {
    if (activeSuite === "all") return [];
    const suite = suites.find(s => s.name === activeSuite);
    return suite ? suite.series : [];
  }, [activeSuite, suites]);

  const toggleSeries = (series) => {
    setExpandedSeries(prev => ({ ...prev, [series]: !prev[series] }));
  };

  const stats = useMemo(() => ({
    total: filteredCourses.length,
    ilt: filteredCourses.filter(c => c.course_type === "ilt").length,
    elearning: filteredCourses.filter(c => c.course_type.includes("scorm")).length,
    tts: courses.filter(c => !c.source || c.source === "tts").length,
    company: courses.filter(c => c.source === "tenant").length,
  }), [filteredCourses, courses]);

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  return (
    <AdminLayout title="Course Catalog" variant={variant}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="catalog-title">
              Browse Catalogue
            </h2>
            <p className="text-muted-foreground">
              {stats.total} courses across {suites.length} catalog suites — Includes subscription &amp; premium add-on courses
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="icon"
              className="h-9 w-9"
              onClick={() => setViewMode("grid")}
              data-testid="view-grid-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="icon"
              className="h-9 w-9"
              onClick={() => setViewMode("list")}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Source Filter — Subscription vs All */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="source-filter">
          <span className="text-sm text-muted-foreground mr-1">Browse:</span>
          {[
            { value: "all", label: "All Courses", icon: BookOpen, count: courses.length },
            { value: "tts", label: "TTS Full Catalog", icon: GraduationCap, count: stats.tts },
            { value: "company", label: "Your Org Courses", icon: Building2, count: stats.company },
            { value: "premium", label: "Premium / Add-on", icon: Sparkles, count: courses.filter(c => c.is_premium || c.course_type === "ilt").length },
          ].map(f => (
            <Button
              key={f.value}
              variant={filterSource === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterSource(f.value)}
              className="gap-1.5"
              data-testid={`source-${f.value}`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
              <Badge variant="secondary" className="ml-1 text-xs">{f.count}</Badge>
            </Button>
          ))}
        </div>

        {/* Suite Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="suite-tabs">
          <Button
            variant={activeSuite === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => { setActiveSuite("all"); setActiveSeries("all"); }}
            className="gap-1.5"
            data-testid="suite-tab-all"
          >
            <BookOpen className="w-4 h-4" />
            All Courses
            <Badge variant="secondary" className="ml-1 text-xs">{courses.length}</Badge>
          </Button>
          {suites.map((suite) => {
            const config = suiteConfig[suite.name] || suiteConfig["Craft Skills eLearning"];
            const Icon = config.icon;
            return (
              <Button
                key={suite.name}
                variant={activeSuite === suite.name ? "default" : "outline"}
                size="sm"
                onClick={() => { setActiveSuite(suite.name); setActiveSeries("all"); }}
                className="gap-1.5"
                data-testid={`suite-tab-${suite.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {suite.name}
                <Badge variant="secondary" className="ml-1 text-xs">{suite.total_courses}</Badge>
              </Button>
            );
          })}
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by title, code, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="catalog-search-input"
            />
            {searchQuery && (
              <button 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchQuery("")}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {activeSuite !== "all" && currentSeries.length > 0 && (
            <Select value={activeSeries} onValueChange={setActiveSeries}>
              <SelectTrigger className="w-[220px]" data-testid="series-filter">
                <SelectValue placeholder="All Series" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Series</SelectItem>
                {currentSeries.map((s) => (
                  <SelectItem key={s.name} value={s.name}>{s.name} ({s.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px]" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="elearning">eLearning</SelectItem>
              <SelectItem value="ilt">ILT (Classroom)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card-3d border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Courses</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card-3d border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.elearning}</p>
                <p className="text-xs text-muted-foreground">eLearning</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card-3d border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ilt}</p>
                <p className="text-xs text-muted-foreground">ILT (Classroom)</p>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card-3d border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{courses.filter(c => !c.out_of_package).length}</p>
                <p className="text-xs text-muted-foreground">In Subscription</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Content */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading catalog...</div>
        ) : filteredCourses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No courses found</h3>
              <p className="text-muted-foreground">Try adjusting your filters or search query</p>
            </CardContent>
          </Card>
        ) : activeSuite !== "all" ? (
          /* Grouped by Series view */
          <div className="space-y-4">
            {Object.entries(groupedBySeries).sort(([a], [b]) => a.localeCompare(b)).map(([series, seriesCourses]) => (
              <Card key={series} className="overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                  onClick={() => toggleSeries(series)}
                  data-testid={`series-toggle-${series.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                      <BookOpen className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{series}</h3>
                      <p className="text-sm text-muted-foreground">{seriesCourses.length} courses</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expandedSeries[series] !== false ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {expandedSeries[series] !== false && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-border">
                        {seriesCourses.map((course) => (
                          <div
                            key={course.id}
                            className="flex items-center justify-between p-4 border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                            onClick={() => navigate(`/course/${course.id}`)}
                            data-testid={`catalog-course-${course.id}`}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              {course.code && (
                                <Badge variant="outline" className="shrink-0 font-mono text-xs">{course.code}</Badge>
                              )}
                              <div className="min-w-0">
                                <h4 className="font-medium truncate">{course.title}</h4>
                                <p className="text-sm text-muted-foreground truncate max-w-xl">{course.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0 ml-4">
                              <Badge className={course.course_type.includes("scorm") ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-orange-500/10 text-orange-600 border-orange-500/20"}>
                                {courseTypeLabels[course.course_type]}
                              </Badge>
                              <div className="text-sm text-muted-foreground flex items-center gap-1 w-16 justify-end">
                                <Clock className="w-3 h-3" />
                                {course.duration_hours}h
                              </div>
                              <div className="font-semibold w-20 text-right">
                                {course.out_of_package ? (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/30">Add-on</Badge>
                                ) : (
                                  <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">Included</Badge>
                                )}
                              </div>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={(e) => { e.stopPropagation(); setShareCourse(course); }} data-testid={`catalog-series-share-${course.id}`} title="Share">
                                <Share2 className="w-3.5 h-3.5" />
                              </Button>
                              {isTenantAdmin && (
                                <Button size="sm" variant="default" className="text-xs h-7 gap-1 bg-[#FF7A59] hover:bg-[#FF8A6C] text-white" onClick={(e) => { e.stopPropagation(); setAssignCourse(course); }} data-testid={`catalog-series-assign-${course.id}`}>
                                  <UserPlus className="w-3 h-3" /> Assign
                                </Button>
                              )}
                              {course.course_type === "ilt" && (
                                <Button size="sm" variant="outline" className="text-xs h-7 gap-1 border-primary/30 text-primary" onClick={(e) => { e.stopPropagation(); setBookCourse(course); }} data-testid={`catalog-series-book-${course.id}`}>
                                  <Calendar className="w-3 h-3" /> Book
                                </Button>
                              )}
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            ))}
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCourses.map((course) => {
              const TypeIcon = courseTypeIcons[course.course_type] || BookOpen;
              const suiteConf = suiteConfig[course.catalog_suite] || suiteConfig["Craft Skills eLearning"];
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card
                    className="card-hover overflow-hidden cursor-pointer h-full flex flex-col"
                    onClick={() => navigate(`/course/${course.id}`)}
                    data-testid={`catalog-card-${course.id}`}
                  >
                    <div className="h-32 relative overflow-hidden">
                      {course.thumbnail_url ? (
                        <div className="absolute inset-0" style={{
                          background: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)), url(${course.thumbnail_url}) center/cover no-repeat`
                        }} />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <TypeIcon className="w-12 h-12 text-white/15" />
                      </div>
                      <div className="absolute top-2.5 left-2.5 flex gap-1.5">
                        <Badge className={course.course_type.includes("scorm") ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs" : "bg-orange-500/20 text-orange-300 border-orange-500/30 text-xs"}>
                          {courseTypeLabels[course.course_type]}
                        </Badge>
                      </div>
                      <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-black/50 px-2 py-0.5 rounded text-white text-xs">
                        <Clock className="w-3 h-3" />
                        {course.duration_hours}h
                      </div>
                      {course.code && (
                        <div className="absolute top-2.5 right-2.5 bg-black/50 px-2 py-0.5 rounded text-white/80 text-xs font-mono">
                          {course.code}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4 flex-1 flex flex-col">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Badge variant="outline" className="text-xs">{course.catalog_suite}</Badge>
                      </div>
                      <h3 className="font-semibold text-sm mb-1.5 line-clamp-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                        {course.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2 flex-1">
                        {course.description}
                      </p>
                      <div className="flex items-center justify-between pt-2.5 border-t border-border">
                        <span className="text-sm font-medium">
                          {course.course_type === "ilt" ? (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/30">Purchase Separately</Badge>
                          ) : course.out_of_package ? (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/30">Add-on</Badge>
                          ) : (
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">Included</Badge>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          {course.enrolled_count > 0 && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Users className="w-3 h-3" /> {course.enrolled_count}
                            </span>
                          )}
                          {course.course_type === "ilt" && (
                            <Button size="sm" variant="outline" className="text-xs h-7 gap-1 border-primary/30 text-primary" onClick={(e) => { e.stopPropagation(); setBookCourse(course); }} data-testid={`catalog-book-${course.id}`}>
                              <Calendar className="w-3 h-3" /> Book Session
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={(e) => { e.stopPropagation(); setShareCourse(course); }} data-testid={`catalog-share-${course.id}`} title="Share">
                            <Share2 className="w-3.5 h-3.5" />
                          </Button>
                          {isTenantAdmin && (
                            <Button size="sm" variant="default" className="text-xs h-7 gap-1 bg-[#FF7A59] hover:bg-[#FF8A6C] text-white" onClick={(e) => { e.stopPropagation(); setAssignCourse(course); }} data-testid={`catalog-assign-${course.id}`}>
                              <UserPlus className="w-3 h-3" /> Assign
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-primary gap-1 h-8 text-xs">
                            Details <ChevronRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <Card>
            <div className="divide-y divide-border">
              {filteredCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/course/${course.id}`)}
                  data-testid={`catalog-list-${course.id}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {course.code && (
                      <Badge variant="outline" className="shrink-0 font-mono text-xs">{course.code}</Badge>
                    )}
                    <div className="min-w-0">
                      <h4 className="font-medium truncate">{course.title}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{course.catalog_suite}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{course.series}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <Badge className={course.course_type.includes("scorm") ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-orange-500/10 text-orange-600 border-orange-500/20"}>
                      {courseTypeLabels[course.course_type]}
                    </Badge>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 w-16 justify-end">
                      <Clock className="w-3 h-3" />
                      {course.duration_hours}h
                    </div>
                    <div className="font-semibold w-20 text-right">{course.out_of_package ? <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/30">Add-on</Badge> : <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs">Included</Badge>}</div>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={(e) => { e.stopPropagation(); setShareCourse(course); }} data-testid={`catalog-list-share-${course.id}`} title="Share">
                      <Share2 className="w-3.5 h-3.5" />
                    </Button>
                    {isTenantAdmin && (
                      <Button size="sm" variant="default" className="text-xs h-7 gap-1 bg-[#FF7A59] hover:bg-[#FF8A6C] text-white" onClick={(e) => { e.stopPropagation(); setAssignCourse(course); }} data-testid={`catalog-list-assign-${course.id}`}>
                        <UserPlus className="w-3 h-3" /> Assign
                      </Button>
                    )}
                    {course.course_type === "ilt" && (
                      <Button size="sm" variant="outline" className="text-xs h-7 gap-1 border-primary/30 text-primary" onClick={(e) => { e.stopPropagation(); setBookCourse(course); }} data-testid={`catalog-list-book-${course.id}`}>
                        <Calendar className="w-3 h-3" /> Book
                      </Button>
                    )}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Share Course Dialog */}
      <ShareDialog
        open={!!shareCourse}
        onOpenChange={(v) => { if (!v) setShareCourse(null); }}
        itemType="course"
        itemId={shareCourse?.id || ""}
        itemTitle={shareCourse?.title || ""}
      />

      {/* Book ILT Session Dialog */}
      <BookSessionDialog
        open={!!bookCourse}
        onOpenChange={(v) => { if (!v) setBookCourse(null); }}
        courseId={bookCourse?.id || ""}
        courseTitle={bookCourse?.title || ""}
      />

      {/* Assign Course Modal (Tenant Admin only) */}
      {assignCourse && (
        <AssignmentModal
          open={!!assignCourse}
          onOpenChange={(v) => { if (!v) setAssignCourse(null); }}
          title={assignCourse?.title || ""}
          onAssign={async (userIds) => {
            if (!assignCourse) return;
            try {
              const results = await Promise.allSettled(
                userIds.map(uid =>
                  api.post("/enrollments", { user_id: uid, course_id: assignCourse.id })
                )
              );
              const success = results.filter(r => r.status === "fulfilled").length;
              const failed = results.filter(r => r.status === "rejected").length;
              if (success > 0) toast.success(`Enrolled ${success} learner${success > 1 ? "s" : ""} successfully`);
              if (failed > 0) toast.error(`${failed} enrollment${failed > 1 ? "s" : ""} failed (may already be enrolled)`);
            } catch (err) {
              toast.error("Assignment failed");
            }
          }}
        />
      )}
    </AdminLayout>
  );
};

export default CatalogPage;
